import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

// Replaces window.alert / window.confirm.
//
// Those are genuinely bad here, not merely unfashionable: they block the JS
// thread, can't be styled or branded, render as an OS chrome box that looks
// like the site broke, are suppressed entirely in some embedded browsers
// (so a confirm() returns false and the user's delete silently does
// nothing), and are unreachable to a test that isn't stubbing globals.
//
// confirm() is promise-based on purpose, so call sites keep the shape they
// already had:
//     if (!window.confirm('Delete?')) return;
//     if (!(await confirm({ title: 'Delete?' }))) return;

const UIFeedbackContext = createContext(null);

export function useUIFeedback() {
  const ctx = useContext(UIFeedbackContext);
  if (!ctx) throw new Error('useUIFeedback must be used inside <UIFeedbackProvider>');
  return ctx;
}

const TOAST_MS = 4000;

export function UIFeedbackProvider({ children }) {
  const [toast, setToast] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const toastTimer = useRef(null);
  const resolverRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const inputRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const notify = useCallback((message, { tone = 'info' } = {}) => {
    clearTimeout(toastTimer.current);
    // A key forces a remount so the slide-up animation replays even when one
    // toast immediately replaces another.
    setToast({ message, tone, key: Date.now() });
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const confirm = useCallback((options = {}) => {
    lastFocusedRef.current = document.activeElement;
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({
        title: options.title || 'Are you sure?',
        body: options.body || '',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        tone: options.tone || 'default',
        input: null,
      });
    });
  }, []);

  // Replaces window.prompt, which is worse than alert/confirm: Chrome blocks
  // it outright inside cross-origin iframes and it is on a deprecation path.
  // Resolves to the trimmed string, or null if cancelled -- matching what
  // window.prompt returned, so call sites keep their `=== null` check.
  const promptText = useCallback((options = {}) => {
    lastFocusedRef.current = document.activeElement;
    setInputValue(options.defaultValue || '');
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({
        title: options.title || '',
        body: options.body || '',
        confirmLabel: options.confirmLabel || 'Submit',
        cancelLabel: options.cancelLabel || 'Cancel',
        tone: options.tone || 'default',
        input: {
          placeholder: options.placeholder || '',
          maxLength: options.maxLength || 500,
          required: options.required !== false,
        },
      });
    });
  }, []);

  const settle = useCallback((answer) => {
    setDialog(null);
    setInputValue('');
    const resolve = resolverRef.current;
    resolverRef.current = null;
    // Return focus to whatever opened the dialog, or a keyboard user is
    // dumped back at the top of the document.
    if (lastFocusedRef.current?.focus) lastFocusedRef.current.focus();
    if (resolve) resolve(answer);
  }, []);

  // Escape cancels; Tab is trapped inside the panel so focus can't wander
  // into the page behind an open modal.
  useEffect(() => {
    if (!dialog) return undefined;
    // Focus the text field when there is one -- that's what the user came to
    // do -- otherwise the confirm button.
    if (dialog.input) inputRef.current?.focus();
    else confirmBtnRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        settle(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        document.querySelectorAll('.modal-panel button, .modal-panel input')
      ).filter((el) => !el.disabled);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dialog, settle]);

  return (
    <UIFeedbackContext.Provider value={{ notify, confirm, promptText }}>
      {children}

      {toast && (
        <div
          key={toast.key}
          className={`toast toast-${toast.tone}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      )}

      {dialog && (
        <div
          className="modal-backdrop"
          onMouseDown={(e) => {
            // Only a click that both starts and ends on the backdrop
            // dismisses; a drag that began inside the panel must not.
            if (e.target === e.currentTarget) settle(false);
          }}
        >
          <div
            className="modal-panel"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            aria-describedby={dialog.body ? 'modal-body' : undefined}
          >
            <h2 className="modal-title" id="modal-title">{dialog.title}</h2>
            {dialog.body && (
              <p className="modal-body" id="modal-body">{dialog.body}</p>
            )}

            {dialog.input && (
              <input
                ref={inputRef}
                className="form-input"
                type="text"
                value={inputValue}
                placeholder={dialog.input.placeholder}
                maxLength={dialog.input.maxLength}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  // Enter submits, matching window.prompt. Guarded on the
                  // required check so it can't submit an empty value that
                  // the button itself would refuse.
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (dialog.input.required && !inputValue.trim()) return;
                  settle(inputValue.trim());
                }}
                style={{ marginBottom: 'var(--space-lg)' }}
              />
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => settle(dialog.input ? null : false)}
              >
                {dialog.cancelLabel}
              </button>
              <button
                type="button"
                ref={confirmBtnRef}
                className={`btn ${dialog.tone === 'danger' ? 'btn-danger' : 'btn-primary'}`}
                disabled={Boolean(dialog.input?.required) && !inputValue.trim()}
                onClick={() => settle(dialog.input ? inputValue.trim() : true)}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </UIFeedbackContext.Provider>
  );
}
